import { createColumnHelper } from "@tanstack/react-table";
import _isEqual from "lodash/isEqual";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Label, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable, usePermanentRealtimeDealers } from "components";
import { usePermanentRealTimeUser, usePrevious, useRegisterNavAction } from "hooks";
import { ROLES, USER_STATUSES } from "models";
import { EditUserModal } from "modules/UsersDashboard/components/EditUserModal";
import { useGetUsersDashboardFilters } from "modules/UsersDashboard/components/UsersDashboardFilters/hooks";
import { useUsersDashboardTableFilterHandlers } from "modules/UsersDashboard/components/UsersDashboardTable/hooks";
import "modules/UsersDashboard/components/UsersDashboardTable/UsersDashboardTable.scss";
import { useUserRolesQuery, useUsersQuery } from "modules/UsersDashboard/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { User } from "models";

export const isEditingRestrictedBetweenRoles = (currentUserRole: ROLES, selectedUserRole: ROLES) => {
  if (currentUserRole === ROLES.SuperAdmin) return false;
  if (currentUserRole === ROLES.ClaireAdmin) return [ROLES.SuperAdmin].includes(selectedUserRole);
  if (currentUserRole === ROLES.SupportAdmin) return [ROLES.SuperAdmin, ROLES.ClaireAdmin].includes(selectedUserRole);
  if (currentUserRole === ROLES.DealerAdmin) return [ROLES.SuperAdmin, ROLES.SupportAdmin, ROLES.ClaireAdmin].includes(selectedUserRole);

  return true;
};

const columnHelper = createColumnHelper<User>();

export const UsersDashboardTable = () => {
  const { t } = useTranslation();

  const [editUserModalData, setEditUserModalData] = useState<number | null>(null);
  const { dealers } = usePermanentRealtimeDealers();
  const { filters } = useGetUsersDashboardFilters();
  const previousFilters = usePrevious(filters);
  const { data: userRoles } = useUserRolesQuery();
  const { isUsersLoading, nbItems, nbPages, users, refetch: refetchUsers } = useUsersQuery();
  const { handlePageSizeChange, handlePaginationChange } = useUsersDashboardTableFilterHandlers();
  const { user } = usePermanentRealTimeUser();

  const handleCloseEditUserModal = () => setEditUserModalData(null);
  const handleOpenEditUserModal = (id: number) => setEditUserModalData(id);

  const handleUserRowClick = (rowUser: User) => {
    const currentUserRole = user?.role_id;
    const selectedUserRole = rowUser.role_id;
    if (!currentUserRole || isEditingRestrictedBetweenRoles(currentUserRole, selectedUserRole)) return;

    handleOpenEditUserModal(rowUser.id);
  };

  useRegisterNavAction(refetchUsers);

  useEffect(() => {
    if (!_isEqual(filters, previousFilters)) refetchUsers();
  }, [filters, previousFilters]);

  useEffect(() => {
    if (filters.dealer_ids.length > 0 || filters.dealer_location_ids.length > 0) refetchUsers();
  }, []);

  const { page: currentPage, page_size: pageSize } = filters;
  const tablePagination = nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined;

  const isDpoUser = Boolean(users.find(u => u.id === editUserModalData)?.dpo);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row, idx) => ({ row, idx }), {
        id: "index",
        header: () => <span className="UsersDashboardTable__table-index"> # </span>,
        size: 30,
        cell: ({
          cell: {
            row: {
              original: { status },
              index
            }
          }
        }) => {
          const rowIdx = index + 1;
          const userNumberOnPage = currentPage > 1 ? (currentPage - 1) * pageSize + rowIdx : rowIdx;

          return (
            <div
              className={`UsersDashboardTable__table-user-container ${status === USER_STATUSES.UserActive ? "UsersDashboardTable__table-active-user-container" : "UsersDashboardTable__table-inactive-user-container"}`}
            >
              <span className={status === USER_STATUSES.UserActive ? "UsersDashboardTable__table-active-user-text" : "UsersDashboardTable__table-inactive-user-text"}>
                {userNumberOnPage}
              </span>
            </div>
          );
        }
      }),

      columnHelper.accessor(row => row, {
        id: "paired",
        header: () => <Icon className="linkify" />,
        size: 30,
        cell: ({
          row: {
            original: { planit_planning_mechanic_id, onei_planning_monteur_id, dms_nr }
          }
        }) => {
          if (!planit_planning_mechanic_id && !onei_planning_monteur_id && !dms_nr) return null;
          return <Icon name="linkify" color="green" />;
        }
      }),

      columnHelper.accessor(row => row, {
        id: "name",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_name", "Name")} </span>,
        size: 170,
        cell: ({
          row: {
            original: { status, first_name, last_name, dpo }
          }
        }) => (
          <div
            className={`UsersDashboardTable__table-user-name-container ${status === USER_STATUSES.UserActive ? "UsersDashboardTable__table-active-user-name-text" : "UsersDashboardTable__table-inactive-user-name-text"}`}
          >
            <span>
              {first_name} {last_name}
            </span>

            {dpo && (
              <div className="UsersDashboardTable__table-dpo-container">
                <Label size="mini" color="blue">
                  DPO
                </Label>
              </div>
            )}
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "username",
        header: () => <span className="UserDashboardTable__table-header-text"> {t("v8_username_or_email", "Username / Email")} </span>,
        size: 40,
        cell: ({
          row: {
            original: { username }
          }
        }) => (
          <div className="username-container">
            <Popup hoverable content={username} trigger={<p className="users-ellipsis">{username}</p>} />
          </div>
        )
      }),

      columnHelper.accessor(row => row, {
        id: "dealer",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_dealer", "Dealer")} </span>,
        cell: ({
          row: {
            original: { dealer_id }
          }
        }) => <span className="no-wrap-text">{dealers.find(({ id }) => id === dealer_id)?.name ?? ""}</span>,
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "location",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_location", "Location")} </span>,
        cell: ({
          row: {
            original: { dealer_location_id, dealer_id }
          }
        }) => {
          const dealer = dealers.find(element => element.id === dealer_id);
          if (!dealer) return "";

          return <span>{dealer.locations?.find(({ id }) => id === dealer_location_id)?.name ?? ""}</span>;
        },
        size: 100
      }),

      columnHelper.accessor(row => row, {
        id: "role",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_role", "Role")} </span>,
        cell: ({
          row: {
            original: { role_id }
          }
        }) => <span>{userRoles?.find(({ id }) => id === role_id)?.name ?? ""}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "sso_email",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_sso_email", "SSO Email")} </span>,
        size: 30,
        cell: ({
          row: {
            original: { email }
          }
        }) => <span>{email}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "phone",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_phone", "Phone")} </span>,
        size: 130,
        cell: ({
          row: {
            original: { phone }
          }
        }) => <span>{phone}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "leased",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_lease", "Lease")} </span>,
        size: 130,
        cell: ({
          row: {
            original: { has_leased_tablet }
          }
        }) => {
          if (!has_leased_tablet) return null;

          return (
            <div className="UsersDashboardTable__table-lease-container">
              <Icon className="check circle outline" />
            </div>
          );
        }
      }),

      columnHelper.accessor(row => row, {
        id: "web_version",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_web_version", "Web V")}# </span>,
        size: 30,
        cell: ({
          row: {
            original: { web_version }
          }
        }) => <span className="no-wrap-text">{web_version}</span>
      }),

      columnHelper.accessor(row => row, {
        id: "app_version",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_app_version", "App V")}# </span>,
        cell: ({
          row: {
            original: { app_version }
          }
        }) => <span className="no-wrap-text">{app_version}</span>,
        size: 90
      }),

      columnHelper.accessor(row => row, {
        id: "last_active",
        header: () => <span className="no-wrap-text UserDashboardTable__table-header-text"> {t("v8_last_active", "Last Active")} </span>,
        cell: ({
          row: {
            original: { last_active }
          }
        }) => {
          if (!last_active) return;

          return (
            <span className="no-wrap-text">
              {`${formatDate(last_active, DATE_FORMATS_FNS.dateMonthYear)} | ${formatDate(last_active, DATE_FORMATS_FNS.hoursMinutesSeconds)}`}
            </span>
          );
        },
        size: 90
      })
    ],
    [t, userRoles, dealers, currentPage, pageSize]
  );

  return (
    <>
      <div className="UsersDashboardTable">
        <ReactTable
          isFetching={isUsersLoading}
          columns={columns}
          data={users}
          pageCount={nbPages}
          pageSize={pageSize}
          totalItems={nbItems}
          pagination={tablePagination}
          onChangePageSize={handlePageSizeChange}
          onPaginationChange={handlePaginationChange}
          onRowClick={handleUserRowClick}
        />
      </div>
      {editUserModalData && <EditUserModal userId={editUserModalData} isDpoUser={isDpoUser} onClose={handleCloseEditUserModal} />}
    </>
  );
};
